import { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import Layout from "@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger,
  ContextMenuSeparator,
} from "@/components/ui/context-menu";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Settings,
  ArrowLeft,
  CalendarDays,
  Layers,
  Plus,
  Trash2,
  Save,
  Pencil,
  Clock,
} from "lucide-react";
import {
  format,
  addDays,
  subDays,
  startOfWeek,
  addWeeks,
  subWeeks,
  isSameDay,
  isToday,
  isBefore,
} from "date-fns";
import { toast } from "react-hot-toast";

// Time constants
const START_HOUR = 6;
const END_HOUR = 21;
const SLOT_HEIGHT = 28; // px per 30-min slot
const HOURS = [];
for (let h = START_HOUR; h <= END_HOUR; h++) HOURS.push(h);

function timeToMinutes(t) {
  const [h, m] = t.split(":").map(Number);
  return h * 60 + m;
}

function minutesToTime(m) {
  return `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;
}

function formatHour(h) {
  const ampm = h >= 12 ? "PM" : "AM";
  const display = h % 12 || 12;
  return `${display} ${ampm}`;
}

export default function AvailabilitySettings({ user }) {
  const navigate = useNavigate();
  const [weekStart, setWeekStart] = useState(startOfWeek(new Date(), { weekStartsOn: 1 }));
  const [recurringBlocks, setRecurringBlocks] = useState({});
  const [overrides, setOverrides] = useState([]);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [templateOpen, setTemplateOpen] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState(null);
  const [dragEnd, setDragEnd] = useState(null);
  const [dragDay, setDragDay] = useState(null);
  const [editBlock, setEditBlock] = useState(null); // { date, start, end, type, id }
  const [editForm, setEditForm] = useState({ start: "", end: "" });

  const weekDays = useMemo(() => {
    return Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
  }, [weekStart]);

  useEffect(() => {
    if (user?.role !== "teacher") {
      navigate("/meetings");
      return;
    }
    fetchAll();
  }, []);

  useEffect(() => {
    fetchOverrides();
  }, [weekStart]);

  const fetchAll = async () => {
    try {
      const [availRes, overrideRes, configRes] = await Promise.all([
        axios.get(`${API}/availability/${user.user_id}`),
        axios.get(`${API}/availability-overrides/${user.user_id}`),
        axios.get(`${API}/scheduling-config/${user.user_id}`),
      ]);
      const byDay = {};
      for (let i = 0; i < 7; i++) byDay[i] = [];
      (availRes.data || []).forEach((b) => {
        byDay[b.day_of_week].push({ start: b.start_time, end: b.end_time, id: b.availability_id });
      });
      setRecurringBlocks(byDay);
      setOverrides(overrideRes.data || []);
      setConfig(configRes.data);
    } catch (err) {
      toast.error("Failed to load availability");
    } finally {
      setLoading(false);
    }
  };

  const fetchOverrides = async () => {
    try {
      const res = await axios.get(`${API}/availability-overrides/${user.user_id}`);
      setOverrides(res.data || []);
    } catch (err) { /* ignore */ }
  };

  // Compute available blocks for a specific date
  const getBlocksForDate = useCallback((date) => {
    const dateStr = format(date, "yyyy-MM-dd");
    const dow = date.getDay() === 0 ? 6 : date.getDay() - 1; // 0=Mon

    // Check if entire day is removed
    const dayOverrides = overrides.filter((o) => o.override_date === dateStr);
    const isRemoved = dayOverrides.some((o) => o.override_type === "remove" && !o.start_time);

    let blocks = [];
    if (!isRemoved) {
      // Start with recurring blocks for this day of week
      blocks = (recurringBlocks[dow] || []).map((b) => ({
        start: b.start,
        end: b.end,
        type: "recurring",
      }));
    }

    // Add override "add" blocks
    dayOverrides
      .filter((o) => o.override_type === "add" && o.start_time && o.end_time)
      .forEach((o) => {
        blocks.push({ start: o.start_time, end: o.end_time, type: "override", id: o.override_id });
      });

    return blocks;
  }, [recurringBlocks, overrides]);

  // Handle click on calendar to add/remove availability
  const handleSlotClick = async (date, hour, half) => {
    const dateStr = format(date, "yyyy-MM-dd");
    const clickMin = hour * 60 + (half ? 30 : 0);
    const blocks = getBlocksForDate(date);

    // Check if clicking inside an existing block
    const existingBlock = blocks.find(
      (b) => clickMin >= timeToMinutes(b.start) && clickMin < timeToMinutes(b.end)
    );

    if (existingBlock) {
      // Remove this time - create a "remove" override for the full day then re-add the parts we want
      // Simpler: if it's an override block, delete it. If recurring, add a remove override for the day.
      if (existingBlock.type === "override" && existingBlock.id) {
        try {
          await axios.delete(`${API}/availability-overrides/${existingBlock.id}`);
          toast.success("Availability removed");
          fetchOverrides();
        } catch (err) {
          toast.error("Failed to remove");
        }
      } else {
        // It's a recurring block - add a "remove" override for the whole day
        try {
          await axios.post(`${API}/availability-overrides`, {
            override_date: dateStr,
            override_type: "remove",
            reason: "Removed via calendar",
          });
          toast.success("Day marked as unavailable");
          fetchOverrides();
        } catch (err) {
          toast.error("Failed to update");
        }
      }
    }
    // If not inside a block, don't do anything on single click - use drag instead
  };

  // Drag to create availability
  const handleMouseDown = (date, slotIndex) => {
    const dayIndex = weekDays.findIndex((d) => isSameDay(d, date));
    setIsDragging(true);
    setDragDay(dayIndex);
    setDragStart(slotIndex);
    setDragEnd(slotIndex);
  };

  const handleMouseEnter = (date, slotIndex) => {
    if (!isDragging) return;
    const dayIndex = weekDays.findIndex((d) => isSameDay(d, date));
    if (dayIndex !== dragDay) return;
    setDragEnd(slotIndex);
  };

  const handleMouseUp = async () => {
    if (!isDragging || dragStart === null || dragEnd === null || dragDay === null) {
      setIsDragging(false);
      return;
    }

    const startSlot = Math.min(dragStart, dragEnd);
    const endSlot = Math.max(dragStart, dragEnd) + 1;
    const startMin = START_HOUR * 60 + startSlot * 30;
    const endMin = START_HOUR * 60 + endSlot * 30;
    const date = weekDays[dragDay];
    const dateStr = format(date, "yyyy-MM-dd");

    setIsDragging(false);
    setDragStart(null);
    setDragEnd(null);
    setDragDay(null);

    if (startSlot === endSlot - 1 && endSlot - startSlot < 1) {
      // Single slot click - check if inside existing block for removal
      const blocks = getBlocksForDate(date);
      const clickMin = startMin;
      const existingBlock = blocks.find(
        (b) => clickMin >= timeToMinutes(b.start) && clickMin < timeToMinutes(b.end)
      );
      if (existingBlock) {
        if (existingBlock.type === "override" && existingBlock.id) {
          try {
            await axios.delete(`${API}/availability-overrides/${existingBlock.id}`);
            toast.success("Availability removed");
            fetchOverrides();
          } catch (err) {
            toast.error("Failed to remove");
          }
        } else {
          try {
            await axios.post(`${API}/availability-overrides`, {
              override_date: dateStr,
              override_type: "remove",
              reason: "Removed via calendar",
            });
            toast.success("Day marked as unavailable");
            fetchOverrides();
          } catch (err) {
            toast.error("Failed to update");
          }
        }
        return;
      }
    }

    // Create an add override for this date/time range
    try {
      await axios.post(`${API}/availability-overrides`, {
        override_date: dateStr,
        override_type: "add",
        start_time: minutesToTime(startMin),
        end_time: minutesToTime(endMin),
      });
      toast.success(`Added ${minutesToTime(startMin)} - ${minutesToTime(endMin)}`);
      fetchOverrides();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add availability");
    }
  };

  const handleDeleteBlock = async (block, dateStr) => {
    if (block.type === "override" && block.id) {
      try {
        await axios.delete(`${API}/availability-overrides/${block.id}`);
        toast.success("Availability block deleted");
        fetchOverrides();
      } catch (err) {
        toast.error("Failed to delete");
      }
    } else {
      // Recurring block — add a remove override for this date
      try {
        await axios.post(`${API}/availability-overrides`, {
          override_date: dateStr,
          override_type: "remove",
          reason: "Removed via calendar",
        });
        toast.success("Day marked as unavailable");
        fetchOverrides();
      } catch (err) {
        toast.error("Failed to update");
      }
    }
  };

  const openEditBlock = (block, date) => {
    setEditBlock({ ...block, date: format(date, "yyyy-MM-dd") });
    setEditForm({ start: block.start, end: block.end });
  };

  const handleSaveEdit = async () => {
    if (!editBlock) return;
    if (timeToMinutes(editForm.start) >= timeToMinutes(editForm.end)) {
      toast.error("Start time must be before end time");
      return;
    }

    if (editBlock.type === "override" && editBlock.id) {
      // Delete old override and create new one
      try {
        await axios.delete(`${API}/availability-overrides/${editBlock.id}`);
        await axios.post(`${API}/availability-overrides`, {
          override_date: editBlock.date,
          override_type: "add",
          start_time: editForm.start,
          end_time: editForm.end,
        });
        toast.success("Availability updated");
        setEditBlock(null);
        fetchOverrides();
      } catch (err) {
        toast.error("Failed to update");
      }
    } else {
      // Recurring block — remove the recurring day and add the new time as override
      try {
        await axios.post(`${API}/availability-overrides`, {
          override_date: editBlock.date,
          override_type: "remove",
          reason: "Replaced via edit",
        });
        await axios.post(`${API}/availability-overrides`, {
          override_date: editBlock.date,
          override_type: "add",
          start_time: editForm.start,
          end_time: editForm.end,
        });
        toast.success("Availability updated for this date");
        setEditBlock(null);
        fetchOverrides();
      } catch (err) {
        toast.error("Failed to update");
      }
    }
  };

  // Save recurring template
  const saveRecurringTemplate = async () => {
    setSaving(true);
    try {
      const flatBlocks = [];
      Object.entries(recurringBlocks).forEach(([day, blocks]) => {
        blocks.forEach((b) => {
          flatBlocks.push({ day_of_week: parseInt(day), start_time: b.start, end_time: b.end });
        });
      });
      await axios.put(`${API}/availability/bulk/${user.user_id}`, { blocks: flatBlocks });
      toast.success("Weekly template saved");
      fetchAll();
    } catch (err) {
      toast.error("Failed to save template");
    } finally {
      setSaving(false);
    }
  };

  const addTemplateBlock = (day) => {
    setRecurringBlocks((prev) => ({
      ...prev,
      [day]: [...(prev[day] || []), { start: "09:00", end: "17:00", id: null }],
    }));
  };

  const removeTemplateBlock = (day, idx) => {
    setRecurringBlocks((prev) => ({
      ...prev,
      [day]: prev[day].filter((_, i) => i !== idx),
    }));
  };

  const updateTemplateBlock = (day, idx, field, val) => {
    setRecurringBlocks((prev) => ({
      ...prev,
      [day]: prev[day].map((b, i) => (i === idx ? { ...b, [field]: val } : b)),
    }));
  };

  const saveConfig = async (updates) => {
    try {
      await axios.put(`${API}/scheduling-config`, updates);
      setConfig((prev) => ({ ...prev, ...updates }));
      toast.success("Settings updated");
    } catch (err) {
      toast.error("Failed to update");
    }
  };

  if (loading) {
    return (
      <Layout user={user}>
        <div className="flex justify-center py-24">
          <div className="w-8 h-8 border-4 border-slate-200 border-t-teal-500 rounded-full animate-spin" />
        </div>
      </Layout>
    );
  }

  const DAYS_SHORT = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
  const TIME_OPTS = [];
  for (let h = 6; h <= 21; h++) {
    for (let m = 0; m < 60; m += 15) {
      const v = `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
      const l = new Date(2026, 0, 1, h, m).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
      TIME_OPTS.push({ value: v, label: l });
    }
  }

  const totalSlots = (END_HOUR - START_HOUR) * 2; // 30-min slots

  return (
    <Layout user={user}>
      <div
        className="space-y-6"
        data-testid="availability-settings-page"
        onMouseUp={handleMouseUp}
        onMouseLeave={() => { if (isDragging) handleMouseUp(); }}
      >
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" className="text-slate-500 -ml-2" onClick={() => navigate("/meetings")}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Meetings
            </Button>
            <div>
              <h1 className="font-serif text-3xl font-bold text-slate-900 tracking-tight">Availability</h1>
              <p className="text-slate-500 mt-1">Drag on the calendar to set when students can book</p>
            </div>
          </div>
        </div>

        {/* Week Navigation */}
        <div className="flex items-center justify-between">
          <Button variant="ghost" size="sm" onClick={() => setWeekStart(subWeeks(weekStart, 1))}>
            <ChevronLeft className="w-4 h-4 mr-1" /> Previous
          </Button>
          <div className="text-center">
            <h2 className="text-sm font-semibold text-slate-800">
              {format(weekStart, "MMMM d")} — {format(addDays(weekStart, 6), "MMMM d, yyyy")}
            </h2>
            <button
              className="text-xs text-teal-600 hover:underline mt-0.5"
              onClick={() => setWeekStart(startOfWeek(new Date(), { weekStartsOn: 1 }))}
            >
              Today
            </button>
          </div>
          <Button variant="ghost" size="sm" onClick={() => setWeekStart(addWeeks(weekStart, 1))}>
            Next <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </div>

        {/* Calendar Grid */}
        <Card className="border border-slate-200 shadow-sm overflow-hidden">
          <CardContent className="p-0">
            <div className="flex select-none">
              {/* Time gutter */}
              <div className="w-16 shrink-0 border-r border-slate-100 bg-slate-50/50">
                <div className="h-12 border-b border-slate-100" /> {/* header spacer */}
                {HOURS.map((h) => (
                  <div key={h} className="relative" style={{ height: SLOT_HEIGHT * 2 }}>
                    <span className="absolute -top-2 right-2 text-[10px] text-slate-400 font-medium">
                      {formatHour(h)}
                    </span>
                  </div>
                ))}
              </div>

              {/* Day columns */}
              <div className="flex-1 grid grid-cols-7">
                {weekDays.map((day, dayIdx) => {
                  const blocks = getBlocksForDate(day);
                  const dateStr = format(day, "yyyy-MM-dd");
                  const today = isToday(day);

                  return (
                    <div key={dateStr} className="border-r border-slate-100 last:border-r-0">
                      {/* Day header */}
                      <div className={`h-12 flex flex-col items-center justify-center border-b border-slate-100 ${today ? "bg-teal-50" : ""}`}>
                        <span className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">
                          {format(day, "EEE")}
                        </span>
                        <span className={`text-sm font-bold ${today ? "text-teal-700 bg-teal-600 text-white w-7 h-7 rounded-full flex items-center justify-center" : "text-slate-700"}`}>
                          {format(day, "d")}
                        </span>
                      </div>

                      {/* Time slots */}
                      <div className="relative" style={{ height: totalSlots * SLOT_HEIGHT }}>
                        {/* Hour grid lines */}
                        {HOURS.map((h, i) => (
                          <div
                            key={h}
                            className="absolute w-full border-t border-slate-100"
                            style={{ top: i * SLOT_HEIGHT * 2 }}
                          />
                        ))}

                        {/* Available blocks */}
                        {blocks.map((block, bIdx) => {
                          const startMin = timeToMinutes(block.start);
                          const endMin = timeToMinutes(block.end);
                          const top = ((startMin - START_HOUR * 60) / 30) * SLOT_HEIGHT;
                          const height = ((endMin - startMin) / 30) * SLOT_HEIGHT;
                          const isOverride = block.type === "override";

                          return (
                            <ContextMenu key={bIdx}>
                              <ContextMenuTrigger asChild>
                                <div
                                  className={`absolute left-1 right-1 rounded-md cursor-pointer transition-colors z-[5] ${
                                    isOverride
                                      ? "bg-teal-400/40 border border-teal-400 hover:bg-teal-400/60"
                                      : "bg-teal-200/50 border border-teal-300/50 hover:bg-teal-300/60"
                                  }`}
                                  style={{ top: Math.max(top, 0), height: Math.max(height, 4) }}
                                  title={`${block.start} - ${block.end}${isOverride ? " (date-specific)" : " (recurring)"}`}
                                >
                                  {height >= SLOT_HEIGHT && (
                                    <div className="px-1.5 py-0.5">
                                      <p className="text-[10px] font-semibold text-teal-800 truncate">
                                        {block.start} - {block.end}
                                      </p>
                                      {isOverride && (
                                        <p className="text-[9px] text-teal-600">date override</p>
                                      )}
                                    </div>
                                  )}
                                </div>
                              </ContextMenuTrigger>
                              <ContextMenuContent className="w-48">
                                <ContextMenuItem
                                  onClick={() => openEditBlock(block, day)}
                                  className="gap-2"
                                >
                                  <Pencil className="w-3.5 h-3.5" />
                                  Edit Time
                                </ContextMenuItem>
                                <ContextMenuSeparator />
                                <ContextMenuItem
                                  onClick={() => handleDeleteBlock(block, dateStr)}
                                  className="gap-2 text-red-600 focus:text-red-600"
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                  {isOverride ? "Delete Block" : "Remove for This Day"}
                                </ContextMenuItem>
                              </ContextMenuContent>
                            </ContextMenu>
                          );
                        })}

                        {/* Drag preview */}
                        {isDragging && dragDay === dayIdx && dragStart !== null && dragEnd !== null && (
                          <div
                            className="absolute left-1 right-1 bg-teal-500/30 border-2 border-teal-500 border-dashed rounded-md pointer-events-none z-10"
                            style={{
                              top: Math.min(dragStart, dragEnd) * SLOT_HEIGHT,
                              height: (Math.abs(dragEnd - dragStart) + 1) * SLOT_HEIGHT,
                            }}
                          />
                        )}

                        {/* Invisible slot grid for mouse interaction */}
                        {Array.from({ length: totalSlots }, (_, slotIdx) => (
                          <div
                            key={slotIdx}
                            className="absolute w-full cursor-crosshair hover:bg-teal-100/30"
                            style={{ top: slotIdx * SLOT_HEIGHT, height: SLOT_HEIGHT }}
                            onMouseDown={(e) => {
                              e.preventDefault();
                              handleMouseDown(day, slotIdx);
                            }}
                            onMouseEnter={() => handleMouseEnter(day, slotIdx)}
                          />
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Legend */}
        <div className="flex items-center gap-4 text-xs text-slate-500">
          <div className="flex items-center gap-1.5">
            <div className="w-4 h-3 rounded bg-teal-200/50 border border-teal-300/50" />
            Recurring (weekly template)
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-4 h-3 rounded bg-teal-400/40 border border-teal-400" />
            Date-specific override
          </div>
          <span className="text-slate-400">|</span>
          <span>Drag to add availability · Click a block to remove</span>
        </div>

        {/* Recurring Weekly Template (collapsible) */}
        <Collapsible open={templateOpen} onOpenChange={setTemplateOpen}>
          <Card className="border border-slate-200 shadow-sm">
            <CollapsibleTrigger asChild>
              <CardHeader className="cursor-pointer hover:bg-slate-50 transition-colors pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="font-serif text-lg flex items-center gap-2">
                    <Layers className="w-5 h-5 text-teal-600" />
                    Weekly Template
                    <Badge variant="outline" className="text-[10px] ml-1">Base schedule</Badge>
                  </CardTitle>
                  <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${templateOpen ? "rotate-180" : ""}`} />
                </div>
              </CardHeader>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <CardContent className="space-y-4 pt-0">
                <p className="text-xs text-slate-500">
                  This is your base weekly schedule. Date-specific overrides on the calendar above take priority.
                </p>
                {DAYS_SHORT.map((day, dayIdx) => (
                  <div key={dayIdx} className="flex items-start gap-4">
                    <div className="w-12 pt-2 shrink-0">
                      <span className="text-sm font-semibold text-slate-700">{day}</span>
                    </div>
                    <div className="flex-1 space-y-2">
                      {(recurringBlocks[dayIdx] || []).length === 0 ? (
                        <p className="text-sm text-slate-400 italic pt-2">—</p>
                      ) : (
                        (recurringBlocks[dayIdx] || []).map((block, bIdx) => (
                          <div key={bIdx} className="flex items-center gap-2">
                            <Select value={block.start} onValueChange={(v) => updateTemplateBlock(dayIdx, bIdx, "start", v)}>
                              <SelectTrigger className="w-28 h-8 text-xs"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                {TIME_OPTS.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                              </SelectContent>
                            </Select>
                            <span className="text-slate-400 text-xs">to</span>
                            <Select value={block.end} onValueChange={(v) => updateTemplateBlock(dayIdx, bIdx, "end", v)}>
                              <SelectTrigger className="w-28 h-8 text-xs"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                {TIME_OPTS.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                              </SelectContent>
                            </Select>
                            <Button variant="ghost" size="icon" className="h-7 w-7 text-red-400 hover:text-red-600" onClick={() => removeTemplateBlock(dayIdx, bIdx)}>
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                        ))
                      )}
                      <Button variant="ghost" size="sm" className="text-[11px] text-slate-500 hover:text-teal-600 h-7" onClick={() => addTemplateBlock(dayIdx)}>
                        <Plus className="w-3 h-3 mr-1" /> Add
                      </Button>
                    </div>
                  </div>
                ))}
                <Button
                  data-testid="save-template-btn"
                  className="bg-teal-600 hover:bg-teal-700 text-white rounded-full"
                  onClick={saveRecurringTemplate}
                  disabled={saving}
                  size="sm"
                >
                  <Save className="w-4 h-4 mr-1.5" />
                  {saving ? "Saving..." : "Save Template"}
                </Button>
              </CardContent>
            </CollapsibleContent>
          </Card>
        </Collapsible>

        {/* Scheduling Rules */}
        {config && (
          <Card className="border border-slate-200 shadow-sm">
            <CardHeader className="pb-3">
              <CardTitle className="font-serif text-lg flex items-center gap-2">
                <Settings className="w-5 h-5 text-teal-600" />
                Scheduling Rules
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <ConfigField label="Min Meeting Length" unit="min" value={config.min_duration} onChange={(v) => saveConfig({ min_duration: parseInt(v) })} options={[15, 30, 45, 60]} />
                <ConfigField label="Max Meeting Length" unit="min" value={config.max_duration} onChange={(v) => saveConfig({ max_duration: parseInt(v) })} options={[15, 30, 45, 60, 90]} />
                <ConfigField label="Buffer Between" unit="min" value={config.buffer_time} onChange={(v) => saveConfig({ buffer_time: parseInt(v) })} options={[0, 5, 10, 15, 30]} />
                <ConfigField label="Advance Notice" unit="hrs" value={config.min_notice_hours} onChange={(v) => saveConfig({ min_notice_hours: parseInt(v) })} options={[1, 2, 4, 6, 12, 24, 48]} />
                <ConfigField label="Booking Window" unit="days" value={config.max_advance_days} onChange={(v) => saveConfig({ max_advance_days: parseInt(v) })} options={[7, 14, 21, 30, 60, 90]} />
                <ConfigField label="Slot Increments" unit="min" value={config.slot_increment} onChange={(v) => saveConfig({ slot_increment: parseInt(v) })} options={[15, 30, 60]} />
              </div>
              <Separator />
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-700">Require Approval</p>
                  <p className="text-xs text-slate-400">Students must wait for approval before meetings are confirmed</p>
                </div>
                <Switch
                  data-testid="require-approval-switch"
                  checked={config.require_approval}
                  onCheckedChange={(v) => saveConfig({ require_approval: v })}
                />
              </div>
            </CardContent>
          </Card>
        )}

        {/* Edit Block Dialog */}
        <Dialog open={!!editBlock} onOpenChange={(open) => !open && setEditBlock(null)}>
          <DialogContent className="sm:max-w-sm">
            <DialogHeader>
              <DialogTitle className="font-serif text-xl flex items-center gap-2">
                <Clock className="w-5 h-5 text-teal-600" />
                Edit Availability
              </DialogTitle>
            </DialogHeader>
            {editBlock && (
              <div className="space-y-4 mt-2">
                <p className="text-sm text-slate-500">
                  {editBlock.date} · {editBlock.type === "override" ? "Date-specific" : "Recurring (edit creates date override)"}
                </p>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Start Time</Label>
                    <Input
                      data-testid="edit-block-start"
                      type="time"
                      value={editForm.start}
                      onChange={(e) => setEditForm({ ...editForm, start: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>End Time</Label>
                    <Input
                      data-testid="edit-block-end"
                      type="time"
                      value={editForm.end}
                      onChange={(e) => setEditForm({ ...editForm, end: e.target.value })}
                    />
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button
                    data-testid="save-edit-block-btn"
                    className="flex-1 bg-teal-600 hover:bg-teal-700 text-white rounded-full"
                    onClick={handleSaveEdit}
                  >
                    Save Changes
                  </Button>
                  <Button
                    data-testid="delete-edit-block-btn"
                    variant="outline"
                    className="border-red-300 text-red-600 hover:bg-red-50 rounded-full"
                    onClick={() => {
                      handleDeleteBlock(editBlock, editBlock.date);
                      setEditBlock(null);
                    }}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}

function ConfigField({ label, unit, value, onChange, options }) {
  return (
    <div>
      <Label className="text-xs text-slate-600">{label}</Label>
      <Select value={String(value)} onValueChange={onChange}>
        <SelectTrigger className="mt-1 h-9">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {options.map((opt) => (
            <SelectItem key={opt} value={String(opt)}>
              {opt} {unit}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
